import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { CartIcon } from "./icons";
import { ProductImage } from "./ui/ProductImage";

export interface PublicCollectionItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product image URL
   */
  productImage: string;
  /**
   * Product name (e.g., "Pigalle Denim Jacket - Black")
   */
  productName: string;
  /**
   * Current product number in the edition
   */
  productNumber: number;
  /**
   * Total number of products in the edition
   */
  totalProductNumber: number;
  /**
   * Shopify product URL for "Buy This Item" button
   */
  shopifyUrl: string;
  /**
   * Optional partner link configuration
   */
  partnerLink?: {
    url: string;
    label?: string;
  };
}

/**
 * Public Collection Item component for displaying products in public Trophy Room
 *
 * Displays product image with overlay buttons, followed by product details below.
 * The partner link button is optional. Both buttons open in a new tab.
 *
 * @example
 * ```tsx
 * <PublicCollectionItem
 *   productImage="/images/jacket.jpg"
 *   productName="Pigalle Denim Jacket - Black"
 *   productNumber={24}
 *   totalProductNumber={40}
 *   shopifyUrl="https://anyw3ar.com/products/jacket"
 *   partnerLink={{ url: "https://partner.com", label: "PARTNER'S LINK" }}
 * />
 * ```
 */
export const PublicCollectionItem = React.forwardRef<
  HTMLDivElement,
  PublicCollectionItemProps
>(
  (
    {
      className,
      productImage,
      productName,
      productNumber,
      totalProductNumber,
      shopifyUrl,
      partnerLink,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-[1rem] items-start w-full",
          className
        )}
        {...props}
      >
        {/* Product Image Container with Overlay Buttons */}
        <div className="relative w-full aspect-square">
          {/* Product Image */}
          <ProductImage
            src={productImage}
            alt={productName}
            containerClassName="absolute inset-0 rounded-[0.3125rem]"
          />

          {/* Overlay Buttons (Bottom Right) */}
          <div className="absolute bottom-[1rem] right-[1rem] flex flex-col gap-[0.5rem] items-end z-10">
            {/* Partner Link Button (Optional) */}
            {partnerLink && (
              <a
                href={partnerLink.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="compact-red"
                  size="compact"
                  className="h-[1.25rem] px-3"
                >
                  {partnerLink.label || "PARTNER'S LINK"}
                </Button>
              </a>
            )}

            {/* Buy This Item Button */}
            <a
              href={shopifyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="compact-black"
                size="compact"
                className="h-[1.25rem] px-3"
                leftIcon={<CartIcon size={10} />}
              >
                BUY THIS ITEM
              </Button>
            </a>
          </div>
        </div>

        {/* Product Details (Below Image) */}
        <div className="flex flex-col gap-[0.5rem] items-start w-full">
          {/* Brand Name (ANYW3AR) */}
          <div className="flex flex-col font-montserrat font-normal justify-end leading-[0] shrink-0 text-[0.875rem] text-gray-500 whitespace-nowrap">
            <p className="leading-[1.25rem]">ANYW3AR</p>
          </div>

          {/* Product Name and Edition Number (Same Line) */}
          <div className="flex items-baseline justify-between w-full gap-[1rem]">
            {/* Product Name */}
            <div className="flex flex-col font-montserrat font-bold justify-end leading-[0] flex-1 text-[0.9375rem] text-black">
              <p className="leading-[1.25rem]">
                {productName}
              </p>
            </div>

            {/* Product Number */}
            <div className="flex flex-col font-montserrat font-normal justify-end leading-[0] shrink-0 text-[0px] text-black whitespace-nowrap">
              <p className="leading-[1.25rem]">
                <span className="font-montserrat font-bold text-[1.25rem] text-black">
                  {productNumber}
                </span>
                <span className="text-[0.9375rem]">/{totalProductNumber}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

PublicCollectionItem.displayName = "PublicCollectionItem";
